import React, { useState, useContext } from 'react'
import { Image } from '../shared/Image/Image'
import Lightbox from '../shared/Lightbox/Lightbox'
import { PDPContext } from '../context'
import { ImageCarousel } from '../shared/ImageCarousel/ImageCarousel'

export const ProductGallery = () => {
  const { product } = useContext(PDPContext)
  // Get featured using the first front image or the no angle image
  const [featuredImageIndex, setFeaturedImageIndex] = useState(0)
  const [isLightboxVisible, setIsLightboxVisible] = useState(false)

  const selectImage = (index) => {
    setFeaturedImageIndex(index)
  }

  // if there are no product images, use the manufacturer images as the featured image. If there are no manufacturer images, use a placeholder image.
  // Hierarchy of manufacturer images is HYBRIS_MANUFACTURER, CNET_BRAND_LOGO, then any other image type. This is to ensure that we are using the most relevant image for the product when there are no product images available.
  const manufacturerImages = (() => {
    const images = product?.manufacturer?.images?.length ? product.manufacturer.images : []
    
    // Try to find HYBRIS_MANUFACTURER images first
    const hybrisImages = images.filter((img) => img.type === 'HYBRIS_MANUFACTURER')
    if (hybrisImages.length > 0) return hybrisImages
    
    // Fall back to CNET_BRAND_LOGO images
    const cnetImages = images.filter((img) => img.type === 'CNET_BRAND_LOGO')
    if (cnetImages.length > 0) return cnetImages
    
    // Fall back to the first available image
    return images.length > 0 ? [images[0]] : []
  })()
  
  const hasNoProductImagesAvailable = !!product?.images?.length
    ? (product?.images?.[0]?.images?.LG?.[0]?.typeDescription === 'noimageavailable' ||
    product?.images?.[0]?.images?.MD?.[0]?.typeDescription === 'noimageavailable' ||
    product?.images?.[0]?.images?.SM?.[0]?.typeDescription === 'noimageavailable')
    : true

  const manufacturerImageFallback = hasNoProductImagesAvailable
    ? (() => {
        const mdImages = manufacturerImages.filter(
          (image) => image.size === 'MD'
        )
        if (mdImages.length > 0) {
          return mdImages
        }
        const smImages = manufacturerImages.filter(
          (image) => image.size === 'SM'
        )
        if (smImages.length > 0) {
          return smImages
        }
        return manufacturerImages.length > 0 ? manufacturerImages : []
      })()
    : []

  const hasManufacturerImageFallback = manufacturerImageFallback.length > 0
  const productImages = Array.isArray(product?.images) ? product.images : []
  const safeFeaturedImageIndex =
    !hasNoProductImagesAvailable && productImages.length > 0 &&
    featuredImageIndex >= 0 &&
    featuredImageIndex < productImages.length
      ? featuredImageIndex
      : 0
  const featuredImage =
    !hasNoProductImagesAvailable ? productImages[safeFeaturedImageIndex] : (hasManufacturerImageFallback ? manufacturerImageFallback[0] : null)
  const mdFeaturedImage =
    featuredImage?.images?.MD?.[0] ||
    featuredImage?.images?.LG?.[0] ||
    featuredImage?.images?.SM?.[0] ||
    (hasManufacturerImageFallback
      ? manufacturerImageFallback[0]
      : undefined)
  const altText = `${product?.descriptions?.shortDescription}, ${product?.manufacturer?.name}, ${product?.materialId}, ${mdFeaturedImage?.description || ''}`
  const carouselImages = (product?.images?.length && !hasNoProductImagesAvailable)
    ? product.images
    : manufacturerImageFallback || []
  const isManufacturerImage = (!product?.images?.length || hasNoProductImagesAvailable) && !!manufacturerImageFallback.length
  return (
    <section className="c-product-gallery">
      <Lightbox
        featuredImageIndex={featuredImageIndex}
        images={carouselImages}
        isVisible={isLightboxVisible}
        onClose={() => setIsLightboxVisible(false)}
      />
      <div className="c-product-gallery__container o-grid o-grid--justify-center">
        <div
          className="c-product-gallery__featured-image o-grid__item u-1/1"
          onClick={() => setIsLightboxVisible(true)}
        >
          <Image src={mdFeaturedImage?.url} alt={altText} isManufacturerImage={isManufacturerImage} />
        </div>
        <ImageCarousel
          images={carouselImages}
          selectedIndex={featuredImageIndex}
          onImageSelect={selectImage}
          addItemProp={true}
        />
      </div>
    </section>
  )
}

export default ProductGallery
